import { FilterIcon, SearchIcon } from "@heroicons/react/outline";

import { useTranslation } from "react-i18next";
import Pagination from "../../../components/pagination/Pagination";
import { useInfiniteQuery, useQuery } from "react-query";

import { indexLiaisonsStudents } from "../../../api/endpoints/liaisons/students";
import { FullSpinner } from "../../../components/LoadingSpinner";
import { Link, useNavigate, useLocation } from "react-router-dom";
import StudentModel from "../../../api/models/StudentModel";
import { useEffect, useState } from "react";
import { indexLiaisonTutors } from "../../../api/endpoints/liaisons/tutors";
import TutorModel from "../../../api/models/TutorModel";
import { indexSponsorships } from "../../../api/endpoints/liaisons/sponsorships";
import SponsorshipModel from "../../../api/models/SponsorshipModel";
import SponsorModel from "../../../api/models/SponsorModel";
import AppFilters from "../../../components/app-filters/AppFilters";
import ShowIf from "../../../components/show-if/ShowIf";
import { ApiCommonParams } from "../../../api/@types/apiTypes";
import StudentsTable from "./StudentsTable";
import PlaceHolder from "../../../components/placeHolder.tsx/PlaceHolder";
import { useOrderBy } from "../../../hooks/useOrderBy";
import { ApiPagination } from "../../../api/helpers/apiPagination";

export default function RecitationTrackingView(props: any) {
  const LocalBasePath = "page.liaison.dashboard.readers";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const resetCurrentPage = () => {
    setCurrentPage(1);
  };
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<{
    [key: string]: any;
  }>({});
  const { orderBy, ...sortingHandlers } = useOrderBy();
  let [currentPageFilter, setCurrentPageFilter] = useState<{
    [key: string]: any;
  }>({
    currentTutorsPage: 1,
    currentSponsorshipPage: 1,
  });
  const { data: students, isLoading } = useQuery(
    ["scheduledLiaisonStudentsExtended", currentPage, filters, orderBy],
    () => {
      let params: ApiCommonParams = {
        orderBy,
        viewName: "extended",
        filters: {
          ...filters,
          is_scheduled: true,
        },
        pagination: { pageSize: 10, pageNumber: currentPage },
      };
      return indexLiaisonsStudents(params);
    },
    {
      staleTime: 0,
      select: (response) => {
        if (response.isSuccess) {
          const data: StudentModel[] = StudentModel.fromApiResponse(
            response.data
          );
          const fetchPaginationInfo: ApiPagination | any = response.pagination;
          /**
           *  if we set  state to add pagination data will generate a side effect.
           *  many re-renders. React limits the number of renders to prevent an infinite loop.
           *  so we need to change our return value to involve pagination info also.
           */
          return { data, paginationInfo: fetchPaginationInfo };
        }
      },
    }
  );

  useEffect(() => {
    resetCurrentPage();
  }, [filters, orderBy]);

  /**
   * i'v replaced useQuery with  useInfiniteQuery because we've a problem with old data and new data not able to combine all ofthem
   * also  useInfiniteQuery is a best solution for scrolling event
   */
  const { data: tutors, fetchNextPage: fetchNextPageTutors } = useInfiniteQuery(
    ["liaisonTutors"],
    ({ pageParam = 1 }) => {
      return indexLiaisonTutors({
        pagination: {
          pageSize: 25,
          pageNumber: pageParam,
        },
      });
    },
    {
      select: (data) => {
        let tutorsList: TutorModel[] = [];

        return {
          pages: data.pages
            .map((pageResponse) => {
              if (pageResponse.isSuccess) {
                tutorsList = TutorModel.fromApiResponse(pageResponse.data);
              }
              return tutorsList;
            })
            .flat(),
          pageParams: data.pageParams,
        };
      },
      cacheTime: 0,
    }
  );
  const { data: sponsorships, fetchNextPage: fetchNextPageSponsorship } =
    useInfiniteQuery(
      ["sponsorships"],
      ({ pageParam = 1 }) => {
        return indexSponsorships({
          pagination: {
            pageSize: 25,
            pageNumber: pageParam,
          },
        });
      },
      {
        select: (data) => {
          let sponsorshipsList: SponsorshipModel[] = [];

          return {
            pages: data.pages
              .map((pageResponse) => {
                if (pageResponse.isSuccess) {
                  sponsorshipsList = SponsorshipModel.fromApiResponse(
                    pageResponse.data
                  );
                }
                return sponsorshipsList;
              })
              .flat(),
            pageParams: data.pageParams,
          };
        },
        cacheTime: 0,
      }
    );

  useEffect(() => {
    fetchNextPageTutors({ pageParam: currentPageFilter.currentTutorsPage });
  }, [currentPageFilter.currentTutorsPage]);

  useEffect(() => {
    fetchNextPageSponsorship({
      pageParam: currentPageFilter.currentSponsorshipPage,
    });
  }, [currentPageFilter.currentSponsorshipPage]);
  const tutorsOptions =
    tutors?.pages.map((tutor: TutorModel) => ({
      key: tutor.name,
      value: tutor.id,
    })) || [];

  const sponsorshipOptions =
    sponsorships?.pages.map((sponsorship: SponsorshipModel) => ({
      key: `${sponsorship.sponsor?.name} \\ ${sponsorship.id}`,
      value: `${sponsorship.id}`,
    })) || [];

  const getSponsors = (sponsorships: SponsorshipModel[]) => {
    let sponsors: SponsorModel[] = [];
    sponsorships?.reduce(
      (frequency: { [key: string]: boolean }, sponsorship) => {
        if (!(sponsorship?.sponsor?.id! in frequency)) {
          sponsors.push(sponsorship?.sponsor!);
          frequency[sponsorship?.sponsor?.id!] = true;
        }
        return frequency;
      },
      {}
    );
    return sponsors;
  };
  const sponsorsOptions = getSponsors(sponsorships?.pages || []).map(
    (sponsor) => ({
      key: `${sponsor?.name}`,
      value: `${sponsor?.id}`,
    })
  );

  const onCancelFilters = () => {
    setShowFilters(false);
    setFilters({});
  };

  const hasStudents = !!students?.data && !!students.data.length;
  return (
    <div className="w-full flex flex-col">
      <div className="bg-gray-50 pb-6">
        <div className=" p-4 divide-gray-300 text-gray-900 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
          <h2 className="text-2xl font-semibold">{t("tracking")}</h2>
          <div className="flex gap-2">
            <button
              type="button"
              data-cy="filterRecitation"
              onClick={() => setShowFilters((x) => !x)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
            >
              <div className="flex items-center justify-start gap-2">
                <FilterIcon
                  className="h-5 w-5 text-gray-500"
                  aria-hidden="true"
                />
                {t("filter")}
              </div>
            </button>
          </div>
        </div>
        {/* <div className="relative mx-4">
                  <div className="absolute inset-y-0 start-0 ps-3  flex items-center pointer-events-none">
                    <SearchIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    type="text"
                    name="serarch"
                  id="serarch"
                    className="focus:ring-brandblue-500 focus:border-brandblue-500 block w-full ps-10  sm:text-sm border-gray-300 rounded-md"
                    placeholder={t("search")}
                  />
                </div> */}
        <ShowIf condition={showFilters}>
          {/**
           * you can add (loadingBottomMargin:number) if you need to load data before scrollbar have specific space to end
           */}
          <AppFilters
            className="pt-1 mx-4 flex flex-col md:flex-row gap-4"
            filters={filters}
            setFilters={setFilters}
            onLoadMore={(key: string) =>
              setCurrentPageFilter({
                ...currentPageFilter,
                [key]: ++currentPageFilter[key],
              })
            }
            fields={[
              {
                name: "by_tutor_id",
                label: t("tutorName"),
                options: tutorsOptions,
                paginationLabel: "currentTutorsPage",
              },
              {
                name: "by_sponsor_id",
                label: t("sponsor"),
                options: sponsorsOptions,
              },
              {
                name: "sponsorship_id",
                label: t("sponsorship"),
                options: sponsorshipOptions,
                paginationLabel: "currentSponsorshipPage",
              },
            ]}
            handleButtonClick={onCancelFilters}
          />
        </ShowIf>
      </div>
      <div className="flex flex-col border-t  flex-1  border-gray-200">
        <ShowIf condition={isLoading}>
          <div className="h-60">
            <FullSpinner size={40} />
          </div>
        </ShowIf>
        <ShowIf condition={!isLoading && !hasStudents}>
          <div className="h-60 flex justify-center items-center">
            <PlaceHolder title="noScheduledStudents" />
          </div>
        </ShowIf>
        <ShowIf condition={!isLoading && hasStudents}>
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
              <div>
                <StudentsTable
                  students={students?.data!}
                  {...sortingHandlers}
                />
              </div>
            </div>
          </div>
        </ShowIf>
        <div className="sticky mt-auto bottom-0">
          <Pagination
            hasNext={!!students?.paginationInfo.hasNext}
            hasPrev={!!students?.paginationInfo.hasPrev}
            goToPage={(page: number) => setCurrentPage(page)}
            currentPage={Number(
              students?.paginationInfo.currentPageNumber || 0
            )}
            numberOfPages={Number(students?.paginationInfo.pagesCount || 0)}
          />
        </div>
      </div>
    </div>
  );
}
